/**
 * Baseball bat generator — parametric pro-style wood bat (C271 profile).
 *
 * Geometry is a surface of revolution around the Z axis (Z-up, Z = 0 at
 * the knob bottom, Z = length at the barrel tip). The profile faithfully
 * reproduces the five regions of a regulation wood bat:
 *
 *   Knob    (0 → ~2.5 cm)   Rounded flare; closed pole cap at bottom.
 *   Handle  (~2.5 → ~45 cm) Near-constant grip diameter.
 *   Taper   (~45 → ~75 cm)  Cubic smoothstep from handle to barrel diameter.
 *   Barrel  (~75 → ~84 cm)  Maximum-diameter hitting plateau.
 *   Tip     (~84 → 100%)    Smooth falloff; closed pole cap at end.
 *
 * All proportional landmarks (knob peak, taper start/end, etc.) scale linearly
 * with the `length` parameter from the 34" / 0.864 m MLB reference.
 *
 * Physical references:
 *   MLB rule limit: max barrel Ø ≤ 6.63 cm; total length ≤ 106.7 cm.
 *   Typical pro C271: length 34", barrel Ø 6.35 cm, handle Ø 2.38 cm.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre (matching PoseRef / Blender).
 */

import { buildLatheGeometry } from "./generatorGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  defineGenerator,
  intParam,
  numParam,
  paramsFromSpecs,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface BaseballBatParams extends GeneratorParams {
  length: number;
  barrelDiameter: number;
  handleDiameter: number;
  knobDiameter: number;
  segments: number;
}

const BASEBALL_BAT_SPECS: readonly GeneratorParamSpec[] = [
  {
    id: "length",
    label: "Length",
    type: "number",
    unit: "meters",
    default: 0.864,
    min: 0.813,
    max: 0.914,
    step: 0.0127,
    description:
      'Total bat length. Adult standard: 0.813–0.864 m (32"–34"). Step = ½".',
  },
  {
    type: "section",
    id: "bat-barrel",
    label: "Barrel",
    params: [
      {
        id: "barrelDiameter",
        label: "Barrel Diameter",
        type: "number",
        unit: "meters",
        default: 0.0635,
        min: 0.05,
        max: 0.0663,
        step: 0.001,
        description:
          'Maximum barrel diameter. MLB rule limit: 0.0663 m (2⅝"). Pro standard: 0.0635 m.',
      },
    ],
  },
  {
    type: "section",
    id: "bat-handle",
    label: "Handle",
    params: [
      {
        id: "handleDiameter",
        label: "Handle Diameter",
        type: "number",
        unit: "meters",
        default: 0.0238,
        min: 0.018,
        max: 0.03,
        step: 0.001,
        description: 'Grip region diameter. Pro standard: 0.0238 m (15/16").',
      },
      {
        id: "knobDiameter",
        label: "Knob Diameter",
        type: "number",
        unit: "meters",
        default: 0.053,
        min: 0.038,
        max: 0.065,
        step: 0.002,
        description: "Bottom flare diameter. Always wider than the handle.",
      },
    ],
  },
  {
    type: "section",
    id: "bat-resolution",
    label: "Resolution",
    defaultCollapsed: true,
    params: [
      {
        id: "segments",
        label: "Segments",
        type: "number",
        unit: "count",
        default: 32,
        min: 8,
        max: 128,
        step: 4,
        description:
          "Azimuthal vertex count. 32 gives a clean silhouette; raise for close-up renders.",
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Profile builder
// ---------------------------------------------------------------------------

/**
 * Cubic smoothstep — zero first-derivative at both ends, giving G1 continuity
 * at every region junction.
 */
function ss(t: number): number {
  return t * t * (3 - 2 * t);
}

/**
 * Build the (axialPos, radius) profile for a bat of the given dimensions.
 *
 * All proportional landmarks scale from the 34" (0.864 m) MLB reference:
 *
 *   xKnobPeak  ≈  11 mm — peak knob diameter
 *   xKnobEnd   ≈  25 mm — handle begins
 *   xHandleEnd ≈ 400 mm — taper begins (handle is ~40 % of bat length)
 *   xTaperEnd  ≈ 720 mm — barrel begins (~12 cm of barrel plateau)
 *   xBarrelMid ≈ 800 mm — barrel plateau centre
 *   xBarrelEnd ≈ 840 mm — tip begins
 *
 * Returns ~29 profile points — enough for a visually smooth curve without
 * over-tessellating the handle's straight section.
 */
function buildBatProfile(
  L: number,
  knobR: number,
  handleR: number,
  barrelR: number,
): readonly [number, number][] {
  const REF = 0.864; // 34" reference length
  const k = L / REF; // proportional scale factor

  const xKnobPeak = 0.011 * k;
  const xKnobEnd = 0.025 * k;
  const xHandleEnd = 0.4 * k; // taper starts earlier: gives a proper handle/barrel ratio
  const xTaperEnd = 0.72 * k; // barrel starts at ~72 cm → ~12 cm of barrel plateau
  const xBarrelMid = 0.8 * k;
  const xBarrelEnd = 0.84 * k;

  const pts: [number, number][] = [];

  // ── Knob ─────────────────────────────────────────────────────────────────
  //
  // Closed pole cap at the bottom, then a smooth dome rise to the peak knob
  // diameter, then a smooth fall back to handle diameter.
  //
  // The rise uses sin(π/2·t) rather than smoothstep: sin has a non-zero
  // slope at t = 0, so the geometry fans out steeply from the pole vertex
  // instead of hovering nearly flat — eliminating the spike that a zero-
  // slope rise would produce at the knob bottom.
  //
  pts.push([0, 0]); // pole → closed bottom cap, normals −Z

  // Rise: pole → knobR over [0, xKnobPeak] (4 rings)
  for (let i = 1; i <= 4; i++) {
    const t = i / 4;
    pts.push([xKnobPeak * t, knobR * Math.sin((Math.PI / 2) * t)]);
  }

  // Fall: knobR → handleR over [xKnobPeak, xKnobEnd] (4 rings)
  // Smoothstep gives G1 continuity at both the knob peak and the handle start.
  for (let i = 1; i <= 4; i++) {
    const t = i / 4;
    pts.push([
      xKnobPeak + (xKnobEnd - xKnobPeak) * t,
      knobR + (handleR - knobR) * ss(t),
    ]);
  }
  // Profile is now at (xKnobEnd, handleR).

  // ── Handle ───────────────────────────────────────────────────────────────
  //
  // Constant-diameter cylinder from xKnobEnd to xHandleEnd. Two rings
  // (start already set above, end added here) are sufficient for a straight
  // section — recomputeLoopNormals produces correct radial normals regardless.
  //
  pts.push([xHandleEnd, handleR]);

  // ── Taper ────────────────────────────────────────────────────────────────
  //
  // Cubic smoothstep from handleR to barrelR over [xHandleEnd, xTaperEnd].
  // 10 rings give a smooth curve with no visible kinks in the silhouette.
  //
  for (let i = 1; i <= 10; i++) {
    const t = i / 10;
    pts.push([
      xHandleEnd + (xTaperEnd - xHandleEnd) * t,
      handleR + (barrelR - handleR) * ss(t),
    ]);
  }
  // Profile is now at (xTaperEnd, barrelR).

  // ── Barrel ───────────────────────────────────────────────────────────────
  //
  // Near-constant hitting plateau. A 2.2 % taper from barrel-mid to
  // barrel-end keeps the profile smooth at the tip junction while matching
  // the real-world slight taper found on turned bats.
  //
  pts.push([xBarrelMid, barrelR]);
  pts.push([xBarrelEnd, barrelR * 0.978]);

  // ── Tip ──────────────────────────────────────────────────────────────────
  //
  // Smooth hemisphere-style falloff to a closed pole at the very end.
  //
  // cos(π/2·t) is used rather than (1 − smoothstep): smoothstep has a
  // zero derivative at t = 1, so the rings near the pole hover at nearly
  // zero radius and then drop abruptly — producing a sharp spike. The
  // cosine profile has slope −π/2 at t = 1, meaning it descends steeply
  // into the pole vertex and produces a proper rounded cap.
  //
  for (let i = 1; i <= 5; i++) {
    const t = i / 6;
    pts.push([
      xBarrelEnd + (L - xBarrelEnd) * t,
      barrelR * 0.978 * Math.cos((Math.PI / 2) * t),
    ]);
  }
  pts.push([L, 0]); // pole → closed tip cap, normals +Z

  return pts;
}

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

function buildBaseballBat(
  p: BaseballBatParams,
  _ctx: GeneratorBuildContext,
): GeneratorOutput {
  const L = numParam(p, "length", 0.864);
  const barrelD = numParam(p, "barrelDiameter", 0.0635);
  const handleD = numParam(p, "handleDiameter", 0.0238);
  const knobD = numParam(p, "knobDiameter", 0.053);
  const segs = intParam(p, "segments", 32);

  const barrelR = barrelD / 2;
  const handleR = handleD / 2;
  // Knob must always flare wider than the handle; clamp to at least +2 mm.
  const knobR = Math.max(handleR + 0.002, knobD / 2);

  const profile = buildBatProfile(L, knobR, handleR, barrelR);

  return { kind: "mesh", geometry: buildLatheGeometry(profile, segs) };
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const BASEBALL_BAT_GENERATOR: Generator =
  defineGenerator<BaseballBatParams>({
    id: "baseball_bat",
    label: "Baseball Bat",
    category: "prop",
    paramSpecs: BASEBALL_BAT_SPECS,
    defaultParams: paramsFromSpecs(BASEBALL_BAT_SPECS) as BaseballBatParams,
    build: buildBaseballBat,
  });
